import { Button } from "design-system/atoms/Button"
import { Checkbox } from "design-system/atoms/Checkbox"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "design-system/molecules/Command"
import { ChevronDown } from "lucide-react"
import { useRef } from "react"
import { useTranslation } from "react-i18next"

type Option<T extends string> = {
  value: T
  label: string
}

type Props<T extends string> = {
  allLabel: string
  options: readonly Option<T>[]
  values: T[] | undefined
  onChange: (values: T[] | undefined) => void
  onSearchChange?: (search: string) => void
}

export function BackofficeConversationExperimentsMultiSelect<T extends string>({
  allLabel,
  options,
  values = [],
  onChange,
  onSearchChange,
}: Props<T>) {
  const { t } = useTranslation()
  const seenOptions = useRef(new Map<T, Option<T>>())
  options.forEach((option) => seenOptions.current.set(option.value, option))

  const selectedOptions = values.flatMap((value) => seenOptions.current.get(value) ?? [])
  const [firstSelected, ...otherSelected] = selectedOptions
  const visibleOptions = [
    ...selectedOptions.filter(
      (selected) => !options.some((option) => option.value === selected.value),
    ),
    ...options,
  ]

  function getLabel() {
    if (values.length === 0) return allLabel
    if (!firstSelected) return String(values.length)
    if (otherSelected.length === 0) return firstSelected.label

    return `${firstSelected.label} +${values.length - 1}`
  }

  function onToggle(value: T) {
    const next = values.includes(value)
      ? values.filter((selected) => selected !== value)
      : [...values, value]

    onChange(next.length > 0 ? next : undefined)
  }

  return (
    <Popover onOpenChange={(isOpen) => !isOpen && onSearchChange?.("")}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-9 w-44 shrink-0 justify-between font-normal">
          <span className="truncate text-left">{getLabel()}</span>
          <ChevronDown className="h-5 w-5 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 min-w-56" align="start">
        <Command shouldFilter={!onSearchChange}>
          <CommandInput
            placeholder={t("backoffice.conversationExperiments.filters.search")}
            onValueChange={onSearchChange}
          />
          <CommandList>
            <CommandEmpty className="text-muted-foreground text-center pt-2 text-xs">
              {t("backoffice.conversationExperiments.filters.noResults")}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem value={allLabel} onSelect={() => onChange(undefined)} className="gap-2">
                <Checkbox checked={values.length === 0} />
                <span className="truncate">{allLabel}</span>
              </CommandItem>
              {visibleOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={`${option.value} ${option.label}`}
                  onSelect={() => onToggle(option.value)}
                  className="gap-2"
                >
                  <Checkbox checked={values.includes(option.value)} />
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
